import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Grid, Card, CardContent, CircularProgress,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  Tabs, Tab, useTheme, useMediaQuery, Divider, Button, IconButton
} from '@mui/material';
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';
import { format, subDays, subMonths, startOfMonth, endOfMonth } from 'date-fns';
import RefreshIcon from '@mui/icons-material/Refresh';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PieChartIcon from '@mui/icons-material/PieChart';

const AllAppsDashboard = (props) => {
  const {setLoading, loading} = props;
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [allAppsData, setAllAppsData] = useState([]);
  const [combinedMetrics, setCombinedMetrics] = useState({
    totalRevenue: 0,
    todayRevenue: 0,
    mrr: 0,
    activeSubscriptions: 0,
    todaySubscriptions: 0
  });
  const [appComparison, setAppComparison] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: 'totalRevenue', direction: 'desc' });
  const [timeRange, setTimeRange] = useState('last24');
  const [startDate, setStartDate] = useState(new Date(Date.now() - 24 * 60 * 60 * 1000));
  const [endDate, setEndDate] = useState(new Date());

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#A28BFF', '#FF6B6B', '#4ECDC4', '#C7F464'];

  // This would be your APP_LIST - replace with your actual app list
  const APP_LIST = [
    { id: "674997d8c4aaadc4054b3372", name: "Anesthesia Assistant" },
    { id: "5e98254f2d1ce10e9431f525", name: "Nerve Blocks" },
    { id: "609a8163ddb62a107efb6a5c", name: "US Pain" },
    { id: "60d19b028ac2e540ca51bbdc", name: "VetRA" },
    { id: "60ddae1f7ffdc227199348ce", name: "Interventional Pain" },
    { id: "610280ea01c6203e11f777ea", name: "MSK US Knee" },
    { id: "63a1becbe53caf5ce853f91f", name: "Pocus" },
    { id: "65a7e37abb2a9f5281b10bce", name: "IV Access" },
  ];

  const fetchAllAppsData = () => {
    setLoading(true);
    
    // Create an array of promises for each app's data fetch
    const fetchPromises = APP_LIST.map(app => {
      const params = new URLSearchParams();
      params.append('app_id', app.id);
      
      return fetch(`${process.env.REACT_APP_API_BASE_URL}${process.env.REACT_APP_API_SUBSCRIPTIONS_ENDPOINT}?${params.toString()}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      })
        .then(async res => {
          if (!res.ok) {
            const text = await res.text();
            throw new Error(`HTTP error! status: ${res.status}, body: ${text}`);
          }
          return res.json();
        })
        .then(data => ({
          appId: app.id,
          appName: app.name,
          subscriptions: data.subscriptions || []
        }))
        .catch(err => {
          console.error(`Failed to fetch data for ${app.name}:`, err);
          return {
            appId: app.id,
            appName: app.name,
            subscriptions: []
          };
        });
    });

    // Wait for all fetch operations to complete
    Promise.all(fetchPromises)
      .then(results => {
        setAllAppsData(results);
        processAllAppsData(results);
        setLoading(false);
        setLastRefreshed(new Date());
      })
      .catch(err => {
        console.error("Failed to fetch all apps data:", err);
        setLoading(false);
      });
  };

  useEffect(() => {
    fetchAllAppsData();
  }, []);

  useEffect(() => {
    if (allAppsData.length > 0) {
      processAllAppsData(allAppsData);
    }
  }, [startDate, endDate, timeRange]);

  const processAllAppsData = (appsData) => {
    const today = new Date().toISOString().split('T')[0];
    let totalRevenue = 0;
    let todayRevenue = 0;
    let totalActiveSubscriptions = 0;
    let totalTodaySubscriptions = 0;
    let totalMrr = 0;
    let totalUsers = 0;
    let totalChurned = 0;
    
    // Process metrics for each app
    const appMetrics = appsData.map(appData => {
      const { appId, appName, subscriptions } = appData;
      
      const todaySubs = subscriptions.filter(s => s.purchaseDate?.startsWith(today) || false);
      const activeSubs = subscriptions.filter(s => 
        s.isSubscription &&
        s.isSubscriptionActive &&
        !s.isSandbox &&
        new Date(s.expirationDate) > new Date()
      );
      
      // Filter subscriptions within the date range
      const filteredSubs = subscriptions.filter(sub => {
        const purchaseDate = new Date(sub.purchaseDate);
        return (purchaseDate >= startDate && purchaseDate <= endDate);
      });
      
      // Calculate app-specific metrics
      const appTotalRevenue = subscriptions.reduce((sum, sub) => sum + parseFloat(sub.convertedPrice || 0), 0);
      const appTodayRevenue = todaySubs.reduce((sum, sub) => sum + parseFloat(sub.convertedPrice || 0), 0);
      const appFilteredRevenue = filteredSubs.reduce((sum, sub) => sum + parseFloat(sub.convertedPrice || 0), 0);
      const appMrr = activeSubs.reduce((sum, sub) => {
        const price = parseFloat(sub.convertedPrice || 0);
        const sku = sub.productSku?.toLowerCase();
        if (isNaN(price) || !sku) return sum;
        if (sku.includes("year")) return sum + price / 12;
        if (sku.includes("month")) return sum + price;
        return sum;
      }, 0);
      
      // Calculate retention rate
      const appTotalUsers = subscriptions.filter(s => s.id === s.originalPurchase).length;
      const appChurned = subscriptions.filter(s => 
        !s.isSubscriptionActive && 
        s.id === s.originalPurchase && 
        !s.isSubscriptionRenewable
      ).length;

      // Add to global totals
      totalRevenue += appTotalRevenue;
      todayRevenue += appTodayRevenue;
      totalActiveSubscriptions += activeSubs.length;
      totalTodaySubscriptions += todaySubs.length;
      totalMrr += appMrr;
      totalUsers += appTotalUsers;
      totalChurned += appChurned;
      
      // Return app metrics
      return {
        appId,
        appName,
        totalRevenue: appTotalRevenue,
        filteredRevenue: appFilteredRevenue,
        todayRevenue: appTodayRevenue,
        mrr: appMrr,
        activeSubscriptions: activeSubs.length,
        todaySubscriptions: todaySubs.length,
        subscriptionCount: subscriptions.length,
        filteredSubscriptionCount: filteredSubs.length
      };
    });
    
    // Set combined metrics
    setCombinedMetrics({
      totalRevenue,
      todayRevenue,
      mrr: totalMrr,
      activeSubscriptions: totalActiveSubscriptions,
      todaySubscriptions: totalTodaySubscriptions
    });
    
    // Set app comparison data
    setAppComparison(appMetrics);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleSort = (key) => {
    let direction = 'desc'; // Default to descending for revenue metrics
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const handleTimeRangeChange = (range) => {
    setTimeRange(range);
    let newStartDate;
    const now = new Date();
    
    switch(range) {
      case 'last24':
        newStartDate = new Date(Date.now() - 24 * 60 * 60 * 1000);
        break;
      case '7days':
        newStartDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
        break;
      case '30days':
        newStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        break;
      case '90days':
        newStartDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000);
        break;
      case 'thisMonth':
        newStartDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
        break;
      case 'lastMonth':
        const firstOfLastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
        const endOfLastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59, 59));
        newStartDate = firstOfLastMonth;
        setEndDate(endOfLastMonth);
        break;
      default:
        newStartDate = subDays(new Date(), 30);
    }
    
    setStartDate(newStartDate);
    if (range !== 'lastMonth') {
      setEndDate(new Date());
    }
  };

  // Sort app comparison data
  const sortedAppComparison = React.useMemo(() => {
    let sortableItems = [...appComparison];
    if (sortConfig.key) {
      sortableItems.sort((a, b) => {
        if (a[sortConfig.key] < b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (a[sortConfig.key] > b[sortConfig.key]) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [appComparison, sortConfig]);

  // Prepare data for revenue trend chart
  const prepareRevenueTrendData = () => {
    const monthlyData = {};
    
    allAppsData.forEach(appData => {
      const { appId, appName, subscriptions } = appData;
      
      subscriptions.forEach(sub => {
        const purchaseDate = new Date(sub.purchaseDate);
        if (purchaseDate >= startDate && purchaseDate <= endDate) {
          const monthYear = format(purchaseDate, 'yyyy-MM');
          
          if (!monthlyData[monthYear]) {
            monthlyData[monthYear] = { 
              month: monthYear,
              totalRevenue: 0
            };
            
            // Initialize revenue for each app
            APP_LIST.forEach(app => {
              monthlyData[monthYear][app.id] = 0;
            });
          }
          
          const price = parseFloat(sub.convertedPrice || 0);
          monthlyData[monthYear][appId] += price;
          monthlyData[monthYear].totalRevenue += price;
        }
      });
    });
    
    return Object.values(monthlyData).sort((a, b) => a.month.localeCompare(b.month));
  };

  // Prepare data for revenue by app chart
  const prepareRevenueByAppData = () => {
    const allApps = appComparison.map(app => ({
      name: app.appName,
      value: app.filteredRevenue,
      activeSubscriptions: app.activeSubscriptions
    }));
    const totalRevenue = allApps.reduce((sum, item) => sum + item.value, 0);
    return allApps.sort((a, b) => b.value - a.value).slice(0, 5).map(item => ({
      ...item,
      revenuePercent: totalRevenue > 0 ? (item.value / totalRevenue) * 100 : 0
    }));
  };

  // Prepare data for active subscriptions by app chart
  const prepareActiveSubsByAppData = () => {
    const allApps = appComparison.map(app => ({
      name: app.appName,
      value: app.filteredRevenue,
      activeSubscriptions: app.activeSubscriptions
    }));
    const totalActiveSubs = allApps.reduce((sum, item) => sum + item.activeSubscriptions, 0);
    return allApps.sort((a, b) => b.activeSubscriptions - a.activeSubscriptions).slice(0, 5).map(item => ({
      ...item,
      activeSubsPercent: totalActiveSubs > 0 ? (item.activeSubscriptions / totalActiveSubs) * 100 : 0
    }));
  };

  const revenueTrendData = prepareRevenueTrendData();
  const revenueByAppData = prepareRevenueByAppData();
  const activeSubsByAppData = prepareActiveSubsByAppData();

  return (
    <>
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
        {!isMobile && (
          <Typography variant="h4" component="h1" color="primary">
            All Apps Dashboard
          </Typography>
        )}
        {!isMobile && (
        <Box>
          {lastRefreshed && (
            <Typography variant="body2" color="text.secondary" sx={{ mr: 2, display: 'inline' }}>
              Last refreshed: {lastRefreshed.toLocaleTimeString()}
            </Typography>
          )}
          <IconButton onClick={fetchAllAppsData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
        )}
      </Box>
      {loading ? (<Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    width: '100%',
                  }}
                >
                  <CircularProgress />
                </Box>) : (
        <>
          {/* Time Range Filter */}
          <Paper sx={{ p: 2, mb: 3 }}>
            <Typography variant="subtitle1" sx={{ mb: 1, textAlign: 'left' }}>Time Range</Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {['last24', '7days', '30days', '90days', 'thisMonth', 'lastMonth'].map((range) => (
                <Button 
                  key={range}
                  variant={timeRange === range ? "contained" : "outlined"}
                  size="small"
                  onClick={() => handleTimeRangeChange(range)}
                >
                  {range === 'last24' ? 'Last 24 hours' : 
                   range === '7days' ? 'Last 7 Days' :
                   range === '30days' ? 'Last 30 Days' :
                   range === '90days' ? 'Last 90 Days' :
                   range === 'thisMonth' ? 'This Month' : 'Last Month'}
                </Button>
              ))}
            </Box>
          </Paper>

          {/* Key Metrics Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Today's Revenue
                  </Typography>
                  <Typography variant="h5" component="div">
                    ${combinedMetrics.todayRevenue.toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    {combinedMetrics.todaySubscriptions} Subscriptions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Active Subscriptions
                  </Typography>
                  <Typography variant="h5" component="div">
                    {combinedMetrics.activeSubscriptions}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            {/*<Grid item xs={12} sm={6} md={4} lg={2}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Monthly Recurring Revenue
                  </Typography>
                  <Typography variant="h5" component="div">
                    ${combinedMetrics.mrr.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>*/}
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Period Revenue
                  </Typography>
                  <Typography variant="h5" component="div">
                    ${appComparison.reduce((sum, app) => sum + app.filteredRevenue, 0).toFixed(2)}
                  </Typography>
                  <Typography variant="body2">
                    {timeRange === 'today' ? 'Today' : 
                     timeRange === '7days' ? 'Last 7 Days' :
                     timeRange === '30days' ? 'Last 30 Days' :
                     timeRange === '90days' ? 'Last 90 Days' :
                     timeRange === 'thisMonth' ? 'This Month' : 'Last Month'}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Total All-Time Revenue
                  </Typography>
                  <Typography variant="h5" component="div">
                    ${combinedMetrics.totalRevenue.toFixed(2)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <Card>
                <CardContent>
                  <Typography color="text.secondary" gutterBottom>
                    Apps Ranking
                  </Typography>
                  <Typography variant="h5" component="div">
                    {appComparison.length}
                  </Typography>
                  <Typography variant="body2">
                    Active Applications
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs for different views */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab icon={<CompareArrowsIcon />} label="App Comparison" />
              <Tab icon={<TrendingUpIcon />} label="Revenue Trends" />
              <Tab icon={<PieChartIcon />} label="Revenue Distribution" />
            </Tabs>

            {/* Tab Content */}
            <Box sx={{ p: 3 }} xs={12}>
              {/* App Comparison Tab */}
              {tabValue === 0 && (
                <Box>
                  <TableContainer component={Paper} sx={{ mb: 3 }}>
                    <Table sx={{ minWidth: 650 }} size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>App Name</TableCell>
                          <TableCell 
                            onClick={() => handleSort('totalRevenue')}
                            sx={{ cursor: 'pointer' }}
                          >
                            All-Time Revenue {sortConfig.key === 'totalRevenue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </TableCell>
                          <TableCell 
                            onClick={() => handleSort('filteredRevenue')}
                            sx={{ cursor: 'pointer' }}
                          >
                            Period Revenue {sortConfig.key === 'filteredRevenue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </TableCell>
                          <TableCell 
                            onClick={() => handleSort('activeSubscriptions')}
                            sx={{ cursor: 'pointer' }}
                          >
                            Active Subs {sortConfig.key === 'activeSubscriptions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </TableCell>
                          {/*<TableCell 
                            onClick={() => handleSort('mrr')}
                            sx={{ cursor: 'pointer' }}
                          >
                            MRR {sortConfig.key === 'mrr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </TableCell>*/}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedAppComparison.map((app) => (
                          <TableRow key={app.appId}>
                            <TableCell>{app.appName}</TableCell>
                            <TableCell>${app.totalRevenue.toFixed(2)}</TableCell>
                            <TableCell>${app.filteredRevenue.toFixed(2)}</TableCell>
                            <TableCell>{app.activeSubscriptions}</TableCell>
                            {/*<TableCell>${app.mrr.toFixed(2)}</TableCell>*/}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={6} width={"100%"}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Period Revenue by App
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart
                            data={sortedAppComparison}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="appName" />
                            <YAxis />
                            <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                            <Legend />
                            <Bar dataKey="filteredRevenue" name="Period Revenue" fill="#8884d8" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6} width={"100%"}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Active Subscriptions by App
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart
                            data={sortedAppComparison}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="appName" />
                            <YAxis />
                            <Tooltip />
                            <Legend />
                            <Bar dataKey="activeSubscriptions" name="Active Subscriptions" fill="#82ca9d" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Revenue Trends Tab */}
              {tabValue === 1 && (
                <Box>
                  <Paper sx={{ p: 2, mb: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Monthly Revenue Trend
                    </Typography>
                    <ResponsiveContainer width="100%" height={400}>
                      <LineChart
                        data={revenueTrendData}
                        margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                        <Legend />
                        <Line 
                          type="monotone" 
                          dataKey="totalRevenue" 
                          name="Total Revenue" 
                          stroke="#8884d8" 
                          activeDot={{ r: 8 }} 
                        />
                        {APP_LIST.map((app, index) => (
                          <Line 
                            key={app.id}
                            type="monotone" 
                            dataKey={app.id} 
                            name={app.name} 
                            stroke={COLORS[index % COLORS.length]} 
                          />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </Paper>

                  <Grid container spacing={3} justifyContent={"center"}>
                    <Grid item xs={12} md={6} width={"45%"}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Revenue Growth Comparison
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart
                            data={revenueTrendData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(value) => `$${value.toFixed(2)}`} />
                            <Legend />
                            <Bar dataKey="totalRevenue" name="Total Revenue" fill="#8884d8" />
                          </BarChart>
                        </ResponsiveContainer>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6} width={"45%"}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Key Metrics Analysis
                        </Typography>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body1">
                            <strong>Top Performing App:</strong> {
                              sortedAppComparison.length > 0 ? 
                              sortedAppComparison[0].appName : 'N/A'
                            }
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            ${sortedAppComparison.length > 0 ? sortedAppComparison[0].filteredRevenue.toFixed(2) : '0.00'} in selected period
                          </Typography>
                        </Box>
                        <Box sx={{ mb: 2 }}>
                          <Typography variant="body1">
                            <strong>Fastest Growing App:</strong> {
                              // This is a placeholder - real calculation would require historical data
                              sortedAppComparison.length > 0 ? 
                              sortedAppComparison[0].appName : 'N/A'
                            }
                          </Typography>
                        </Box>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}

              {/* Revenue Distribution Tab */}
              {tabValue === 2 && (
                <Box>
                  <Grid container spacing={3} justifyContent={"center"}>
                    <Grid item xs={12} md={6} width={"100%"}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Revenue Distribution by App
                        </Typography>
                        <ResponsiveContainer width="100%" height={400}>
                          <PieChart>
                            <Pie
                              data={revenueByAppData}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              outerRadius={150}
                              fill="#8884d8"
                              dataKey="value"
                              nameKey="name"
                              label={(entry) => {
                                const dataEntry = revenueByAppData.find(item => item.name === entry.name);
                                return dataEntry ? `${dataEntry.revenuePercent.toFixed(1)}%` : '';
                              }}
                            >
                              {revenueByAppData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value, name, props) => [props.payload.activeSubscriptions, 'Count']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={6} width={"100%"}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Active Subscriptions Distribution
                        </Typography>
                        <ResponsiveContainer width="100%" height={400}>
                          <PieChart>
                            <Pie
                              data={activeSubsByAppData}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              outerRadius={150}
                              fill="#82ca9d"
                              dataKey="activeSubscriptions"
                              nameKey="name"
                              label={(entry) => {
                                const dataEntry = activeSubsByAppData.find(item => item.name === entry.name);
                                return dataEntry ? `${dataEntry.activeSubsPercent.toFixed(1)}%` : '';
                              }}
                            >
                              {activeSubsByAppData.map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [value, 'Count']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} width={"100%"}>
                      <Paper sx={{ p: 2 }}>
                        <Typography variant="h6" gutterBottom>
                          Revenue Breakdown
                        </Typography>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>App Name</TableCell>
                                <TableCell>Revenue</TableCell>
                                <TableCell>% of Total</TableCell>
                                <TableCell>Active Subs</TableCell>
                                <TableCell>% of Total</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {sortedAppComparison.map((app) => {
                                const totalRevenue = appComparison.reduce((sum, a) => sum + a.filteredRevenue, 0);
                                const totalSubs = appComparison.reduce((sum, a) => sum + a.activeSubscriptions, 0);
                                const revenuePercent = totalRevenue > 0 ? (app.filteredRevenue / totalRevenue) * 100 : 0;
                                const subsPercent = totalSubs > 0 ? (app.activeSubscriptions / totalSubs) * 100 : 0;
                                
                                return (
                                  <TableRow key={app.appId}>
                                    <TableCell>{app.appName}</TableCell>
                                    <TableCell>${app.filteredRevenue.toFixed(2)}</TableCell>
                                    <TableCell>{revenuePercent.toFixed(1)}%</TableCell>
                                    <TableCell>{app.activeSubscriptions}</TableCell>
                                    <TableCell>{subsPercent.toFixed(1)}%</TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Box>
          </Paper>
        </>)}
    </Box>
    </>
  );
};

export default AllAppsDashboard;